/**
 * 工具清單 SSOT（Single Source of Truth）
 * 欄位：id, slug, name, description, category, icon（lucide 圖示名稱）
 */
export type ToolCategory =
  | '圖片'
  | '文字'
  | '轉換'
  | '計算'
  | '找好康'
  | 'AI'
  | '其他';

export interface ToolItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: ToolCategory;
  /** lucide-react 圖示名稱，對應到元件 */
  icon: string;
}

export const TOOLS: ToolItem[] = [
  {
    id: 'qr-generator',
    slug: 'qr-generator',
    name: 'QR 產生器',
    description: '輸入文字或網址，即時產生 QR Code 圖片。',
    category: '轉換',
    icon: 'QrCode',
  },
  {
    id: 'image-editor',
    slug: 'image-editor',
    name: '圖片修改器',
    description: '去背、智能裁切、AI 放大、長寬比與輸出格式，一鍵出圖。',
    category: '圖片',
    icon: 'Image',
  },
  {
    id: 'ecommerce-editor',
    slug: 'ecommerce-editor',
    name: '電商編輯器',
    description: '上傳圖片、加文字與裝飾，一鍵輸出電商圖。',
    category: '圖片',
    icon: 'PenTool',
  },
  {
    id: 'html-content-editor',
    slug: 'html-content-editor',
    name: 'HTML 內容編輯器',
    description:
      '拖放圖片設背景或圖層、雙擊新增文字、加白卡，支援多種長寬比，一鍵下載 PNG。',
    category: '圖片',
    icon: 'LayoutTemplate',
  },
  {
    id: 'unit-converter',
    slug: 'unit-converter',
    name: '單位換算',
    description: '長度、重量、面積等單位互換。',
    category: '計算',
    icon: 'Calculator',
  },
  {
    id: 'badminton-team',
    slug: 'badminton-team',
    name: '羽球自動編隊對戰',
    description: '設定參加人員與等級、場地，依編隊方式自動產生雙打對戰表。',
    category: '其他',
    icon: 'UsersRound',
  },
  {
    id: 'regex-tester',
    slug: 'regex-tester',
    name: 'Regex 可視化測試器',
    description: '撰寫並測試正則表達式，即時顯示匹配結果與分組。',
    category: '文字',
    icon: 'Code2',
  },
  {
    id: 'html-preview',
    slug: 'html-preview',
    name: 'HTML 可視化網頁',
    description: '輸入 HTML/CSS/JS，即時預覽網頁效果。',
    category: '文字',
    icon: 'FileCode',
  },
  {
    id: 'compound-loan-calculator',
    slug: 'compound-loan-calculator',
    name: '複利・房貸計算器',
    description: '複利試算、房貸本息攤還、每月還款與總利息一覽。',
    category: '計算',
    icon: 'Calculator',
  },
  {
    id: 'short-url',
    slug: 'short-url',
    name: '短網址生成器',
    description:
      '產生短網址並可查看點擊分析，基於 Cloudflare Workers KV，速度極快。',
    category: '轉換',
    icon: 'Link',
  },
  {
    id: 'line-message-preview',
    slug: 'line-message-preview',
    name: 'LINE 訊息預覽器',
    description: '模擬圖文選單、Flex Message 在手機上的顯示效果。',
    category: '其他',
    icon: 'MessageCircle',
  },
  {
    id: 'pdf-tools',
    slug: 'pdf-tools',
    name: 'PDF 轉檔與壓縮',
    description: '合併、分割、壓縮 PDF，介面乾淨無廣告。',
    category: '轉換',
    icon: 'FileText',
  },
  // 找好康
  {
    id: 'weekend-flight-radar',
    slug: 'weekend-flight-radar',
    name: '週末快閃機票搜尋器',
    description: '從台灣出發，週五去、週日/一回程，三個月內最低票價一覽。',
    category: '找好康',
    icon: 'Plane',
  },
  {
    id: 'lcc-fee-calculator',
    slug: 'lcc-fee-calculator',
    name: '廉航手續費計算器',
    description: '輸入機票原價，加上行李、刷卡手續費，算出最終負擔價格。',
    category: '找好康',
    icon: 'Receipt',
  },
  {
    id: 'hotel-coupon-list',
    slug: 'hotel-coupon-list',
    name: '訂房折扣碼彙整',
    description: 'Agoda、Booking、Klook 當月可用折扣碼一覽，一鍵複製與比價。',
    category: '找好康',
    icon: 'Building2',
  },
  {
    id: 'fast-food-coupon-search',
    slug: 'fast-food-coupon-search',
    name: '速食優惠代碼快搜',
    description: '輸入「麥當勞」「炸雞」「披薩」等，查詢目前可用優惠代碼。',
    category: '找好康',
    icon: 'UtensilsCrossed',
  },
  // AI（Cloudflare Workers AI）
  {
    id: 'ai-drizzle',
    slug: 'ai-drizzle',
    name: '智能 Drizzle 產生器',
    description: '用自然語言描述資料表，AI 產出 Drizzle Schema 程式碼。',
    category: 'AI',
    icon: 'Bot',
  },
  {
    id: 'ai-speech-to-text',
    slug: 'ai-speech-to-text',
    name: '語音轉文字',
    description: '上傳音檔，使用 Whisper 轉成文字或字幕。',
    category: 'AI',
    icon: 'Mic',
  },
  {
    id: 'ai-text',
    slug: 'ai-text',
    name: 'AI 文字小幫手',
    description: '輸入提示與選填系統指令，由 Llama 生成文字。',
    category: 'AI',
    icon: 'Sparkles',
  },
  {
    id: 'audio-trim-silence',
    slug: 'audio-trim-silence',
    name: '音檔去靜音',
    description: '偵測錄音中的長時間無聲區段並刪除，只保留有說話的部分。',
    category: '轉換',
    icon: 'Scissors',
  },
  {
    id: 'video-trim-silence',
    slug: 'video-trim-silence',
    name: '影片去靜音剪輯（實驗性）',
    description:
      '上傳 MP4，依音訊偵測長時間靜音並將整段影片剪掉，盡量維持口型同步。',
    category: '轉換',
    icon: 'Video',
  },
];

/** 依 slug 取得單一工具 */
export function getToolBySlug(slug: string): ToolItem | undefined {
  return TOOLS.find(t => t.slug === slug);
}

/** 取得所有分類（去重、維持順序）；可傳入工具列表，未傳則用 TOOLS */
export function getCategories(toolList: ToolItem[] = TOOLS): ToolCategory[] {
  const set = new Set<ToolCategory>();
  toolList.forEach(t => set.add(t.category));
  return Array.from(set);
}
